import { CAPABILITIES, can } from './roles';
import { plural } from './plural';

const { MANAGE_POLICY_LINES, MANAGE_STAFF_ACCESS, VIEW_ALL_POLICYHOLDERS, CONTRIBUTE_HUB_CONTENT, MANAGE_HUB_CONTENT } = CAPABILITIES;

export const SEQUENCES_NEEDS_RENEWAL_DATES = 'Sequences need policyholders with renewal dates before they can do anything, and none of your policyholders has one yet.';

const BROKER_SUFFIX = ' The owner invites policyholders and decides who each broker looks after.';

export const hubName = (event) => event?.login_event_name || window.store?.subdomain || 'your hub';

const isZero = (value) => value === 0;
const known = (value) => typeof value === 'number';

const action = (id, label, extra = {}) => ({ id, label, ...extra });

const withoutCap = (entry) => Object.fromEntries(Object.entries(entry).filter(([name]) => name !== 'cap'));

const build = (input, state) => {
  const actions = (state.actions ?? [])
    .filter(({ cap }) => !cap || can(input.role, cap, input.capabilities))
    .map(withoutCap);

  return { key: state.key, icon: state.icon, headline: state.headline, body: state.body, actions, ...(state.sentence && { sentence: state.sentence }) };
};

const byReader = (owner, broker = owner) => ({ owner, staff: owner, broker });

const emptyFlag = (state) => (input) => (input.empty === true ? build(input, state) : null);

const notRegisteredYet = (input) => {
  const n = input.counts.not_registered;
  const [has, hasnt] = n === 1 ? ['has', 'hasn’t'] : ['have', 'haven’t'];

  return {
    key: 'not-registered-yet',
    icon: 'mail',
    headline: 'Nobody has registered yet',
    body: `${plural(n, 'person', 'people')} ${has} access to ${input.hubName} but ${hasnt} finished registering. They appear here once they do.`,
    actions: [action('see-not-registered', 'See who hasn’t registered')],
  };
};

const ownerPolicyholders = (input) => {
  const c = input.counts;
  if (!c || input.filtered) return null;
  if (!isZero(c.access_granted) || !isZero(c.not_registered) || !isZero(c.pending_requests)) return null;

  return build(input, {
    key: 'policyholders-none',
    icon: 'users',
    headline: 'No policyholders yet',
    body: 'Invite them by email, or import a list if you already have one. They see nothing until they finish registering.',
    actions: [
      action('invite', 'Invite someone', { primary: true, icon: 'plus', cap: VIEW_ALL_POLICYHOLDERS }),
      action('import', 'Import a list', { cap: VIEW_ALL_POLICYHOLDERS }),
    ],
  });
};

const ownerRegistered = (input) => {
  const c = input.counts;
  if (!c) return null;
  if (known(c.not_registered) && c.not_registered > 0) return build(input, notRegisteredYet(input));

  return build(input, {
    key: 'none-registered',
    icon: 'users',
    headline: 'Nobody has registered yet',
    body: 'People appear here once they finish registering.',
  });
};

const brokerRegistered = (input) => {
  const c = input.counts;
  if (!c) return null;
  if (known(c.not_registered) && c.not_registered > 0) return build(input, notRegisteredYet(input));

  if (c.hub_has_policyholders === false) {
    return build(input, {
      key: 'empty-hub',
      icon: 'users',
      headline: 'Nobody in the hub yet',
      body: `Nobody has joined ${input.hubName} yet. The owner invites policyholders and decides who each broker looks after.`,
    });
  }

  return build(input, {
    key: 'none-assigned',
    icon: 'mail',
    headline: 'Nobody assigned to you yet',
    body: 'The owner decides who each broker looks after. Once somebody is assigned to you they appear here, with their policies and renewal dates.',
  });
};

const brokerOverview = (input) => {
  if (!isZero(input.counts?.policyholders)) return null;

  return build(input, {
    key: 'broker-no-book',
    sentence: 'Nothing needs you yet.',
    icon: 'users',
    headline: 'No policyholders assigned to you yet',
    body: 'The owner assigns policyholders to brokers. Once somebody is yours, this page shows who is waiting on a reply, whose renewal is close, and who has gone quiet.',
    actions: [action('calendar', 'See what’s on this week')],
  });
};

const SESSIONS_BODY = 'A session is a short online talk your policyholders can join — a renewals Q&A, storm preparation, anything worth saying to more than one person at once. Office Hours run every weekday and need no scheduling.';

const officeHoursActions = (input) => {
  if (input.canJoinDesk) return [action('office-hours', 'Open Office Hours', { icon: 'phone' })];
  if (input.hasDesk) return [];

  return [action('office-hours-setup', 'Open Office Hours', { icon: 'phone', cap: MANAGE_HUB_CONTENT })];
};

const sessionsFirstRun = (input) => {
  if (input.everScheduled !== false) return null;

  return build(input, {
    key: 'sessions-first-run',
    icon: 'video',
    headline: 'Nothing scheduled yet',
    body: SESSIONS_BODY,
    actions: [
      action('schedule', 'Schedule a session', { primary: true, icon: 'plus', cap: CONTRIBUTE_HUB_CONTENT }),
      ...officeHoursActions(input),
    ],
  });
};

const sessionsQuietWeek = (input) => {
  if (input.everScheduled !== true) return null;

  return build(input, {
    key: 'sessions-quiet-week',
    icon: 'cal',
    headline: 'Nothing scheduled this week',
    body: input.hasDesk ? 'Office Hours still run every weekday.' : '',
    actions: [action('schedule', 'Schedule one', { icon: 'plus', cap: CONTRIBUTE_HUB_CONTENT })],
  });
};

const communications = (input) => {
  const c = input.counts;
  if (!c || !known(c.access_granted) || !known(c.not_registered)) return null;
  if (c.access_granted + c.not_registered !== 0) return null;

  const body = 'Segments decide who receives what; sequences send renewal reminders on their own. Both need policyholders with renewal dates before they can do anything.';

  return build(input, {
    key: 'communications-none',
    icon: 'mega',
    headline: 'Nothing sent yet',
    body: input.reader === 'broker' ? body + BROKER_SUFFIX : body,
    actions: [action('invite', 'Invite policyholders first', { cap: VIEW_ALL_POLICYHOLDERS })],
  });
};

const content = emptyFlag({
  key: 'content-none',
  icon: 'book',
  headline: 'Nothing shared yet',
  body: 'Files, links and banners you share with policyholders. Upload a policy summary or a claims guide, and choose who can see it.',
  actions: [action('add-file', 'Add a file', { primary: true, icon: 'plus', cap: CONTRIBUTE_HUB_CONTENT })],
});

const policyLines = emptyFlag({
  key: 'policy-lines-none',
  icon: 'shield',
  headline: 'No policy lines yet',
  body: 'The kinds of cover you sell — home, auto, renters. Records, segments and the registration form are all built from these, so this is the first thing to set up.',
  actions: [action('add-line', 'Add a policy line', { primary: true, icon: 'plus', cap: MANAGE_POLICY_LINES })],
});

const recordings = emptyFlag({
  key: 'recordings-none',
  icon: 'film',
  headline: 'No recordings yet',
  body: 'Sessions you record appear here afterwards, with a transcript. Office Hours is never recorded.',
  actions: [action('schedule', 'Schedule a session', { cap: CONTRIBUTE_HUB_CONTENT })],
});

const insights = emptyFlag({
  key: 'insights-none',
  icon: 'chart',
  headline: 'Nothing to measure yet',
  body: 'Reply times, who is at risk of leaving, which sessions people came to. A few weeks of activity and this page starts earning its place.',
});

const none = () => null;

const DECK = {
  overview: byReader(none, brokerOverview),
  policyholders: byReader(ownerPolicyholders, none),
  registered: byReader(ownerRegistered, brokerRegistered),
  sessions: byReader(sessionsFirstRun),
  quietWeek: byReader(sessionsQuietWeek),
  communications: byReader(communications),
  content: byReader(content),
  policyLines: byReader(policyLines),
  recordings: byReader(recordings),
  insights: byReader(insights),
};

export function hubEmptyState(page, input) {
  const pick = DECK[page]?.[input?.reader];

  return pick ? pick(input) : null;
}

const STEPS = [
  {
    id: 'policy-lines',
    title: 'Add your policy lines',
    body: 'Home, auto, renters — whatever you sell. Every policyholder record hangs off these, so they come first.',
    buttons: [{ id: 'policy-lines', label: 'Set them up', primary: true, cap: MANAGE_POLICY_LINES }],
    done: (input) => input.policyLinesDone,
  },
  {
    id: 'invite',
    title: 'Invite your policyholders',
    body: 'By email, or import a list you already have. They see nothing until they finish registering.',
    buttons: [
      { id: 'invite', label: 'Invite', primary: false, cap: VIEW_ALL_POLICYHOLDERS },
      { id: 'import', label: 'Import a list', primary: false, cap: VIEW_ALL_POLICYHOLDERS },
    ],
    done: (input) => (known(input.policyholders) ? input.policyholders > 0 : null),
  },
  {
    id: 'team',
    title: 'Add the brokers who’ll look after them',
    body: 'Each policyholder is assigned one broker. Until then, messages arrive with nobody’s name on them.',
    buttons: [{ id: 'team', label: 'Add your team', primary: false, cap: MANAGE_STAFF_ACCESS }],
    done: (input) => input.brokersDone,
  },
];

const stepFor = (input, step, index) => {
  const done = step.done(input) ?? null;
  const actions = done === true
    ? []
    : step.buttons
      .filter((b) => can(input.role, b.cap, input.capabilities))
      .map(withoutCap);

  return {
    id: step.id,
    n: index + 1,
    title: step.title,
    body: step.body,
    done,
    actions,
  };
};

const SWITCHED_ON = 'Sessions, Office Hours and the portal are already switched on.';

const checklistFooter = (input) => (can(input.role, CONTRIBUTE_HUB_CONTENT, input.capabilities)
  ? {
    footer: `${SWITCHED_ON} Schedule a session now and it will be waiting when policyholders arrive.`,
    footerAction: { id: 'schedule', label: 'Schedule a session' },
  }
  : {
    footer: `${SWITCHED_ON} They just have nobody to show to yet.`,
    footerAction: null,
  });

export function setupChecklist(input) {
  if (!input || input.reader === 'broker') return null;

  return {
    title: `${input.hubName || 'your hub'} is ready`,
    sentence: 'Nothing here yet — three things to set up, then policyholders can come in.',
    steps: STEPS.map((step, index) => stepFor(input, step, index)),
    ...checklistFooter(input),
  };
}
